import { observer } from "mobx-react";
import { WalletStore } from "../../stores/wallet/wallet-store";
import './auto-recharge-settings-modal.scss';
import { DefaultModalLayout } from "../../../../components/modal/layouts/default-modal-layout";
import FontBody1 from "../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../components/fonts/font-props";
import { Button } from "../../../../components/inputs/button/button";
import { BUTTON_VARIANTS } from "../../../../components/inputs/button-variants";
import { Input } from "../../../../components/inputs/input/input";
import WalletBalance from "./wallet-balance";
import FormFieldLineLayout from "../../../../components/layouts/form-field-line-layout/form-field-line-layout";
import { Note, NOTE_TYPE } from "../../../../components/note/note";
import { FontBody2 } from "../../../../components/fonts/font-body2/font-body2";
import { FormField } from "../../../../components/form-field/form-field";

interface IProps {
    walletStore: WalletStore;
    onCloseClick: () => void;
}

const AutoRechargeSettingsModal: React.FC<IProps> = observer(({ walletStore, onCloseClick }) => {
    return (
        <>
            <DefaultModalLayout isCloseDisabled={false} onCloseClicked={() => walletStore.setShouldShowAutoRechargeSettingsModal(false)}>
                <div className="auto-recharge-settings-modal">
                    <div className={'auto-recharge-settings-modal-content'}>
                        <FontBody1 variant={FONT_VARIANT.BOLD}>{"Auto-recharge settings"}</FontBody1>
                        <WalletBalance walletStore={walletStore} showAddCreditsButton={false} />
                        <div className="auto-recharge-settings-modal-content-body-form">
                            <FormFieldLineLayout>
                                <FontBody2>
                                    {`Your saved payment method will be automatically charged when your wallet balance falls below the selected threshold.`}
                                </FontBody2>
                                <div className="auto-recharge-settings-modal-form-field-container">
                                    <div className="auto-recharge-settings-modal-form-field-label">
                                        <FontBody1 variant={FONT_VARIANT.BOLD}>Minimum Balance Threshold</FontBody1>
                                    </div>
                                    <div className="auto-recharge-settings-modal-form-field-input">
                                        <FormField label={<FontBody2>{`Enter amount (minimum ${walletStore.minimumDepositAmountFormattedWithSymbol})`}</FontBody2>} required>
                                            <Input
                                                required
                                                validationCallback={walletStore.validateMinimumThresholdEditor}
                                                adornment={walletStore.walletCurrencySymbol}
                                                value={walletStore.rechargeThreshold}
                                                onChange={walletStore.setMinimumThreshold}
                                                type={'number'}
                                                temp={true}
                                                step={1}
                                                />
                                        </FormField>
                                        {!walletStore.validateMinimumThresholdEditor().isValid && walletStore.validateMinimumThresholdEditor().messages.map(message => (
                                            <FontBody2 key={message} color={FONT_COLOR.SEMANTIC_DANGER}>{message}</FontBody2>
                                        ))}
                                    </div>
                                </div>
                                <div className="auto-recharge-settings-modal-form-field-container">
                                    <div className="auto-recharge-settings-modal-form-field-label">
                                        <FontBody1 variant={FONT_VARIANT.BOLD}>Recharge Amount</FontBody1>
                                    </div>
                                    <div className="auto-recharge-settings-modal-form-field-input">
                                        <FormField label={<FontBody2>{`Enter amount (must be equal to or greater than threshold amount, up to ${walletStore.maximumRechargeAmountFormattedWithSymbol})`}</FontBody2>} required>
                                            <Input
                                                required
                                                validationCallback={walletStore.validateRechargeAmountEditor}
                                                adornment={walletStore.walletCurrencySymbol}
                                                value={walletStore.rechargeAmount}
                                                onChange={walletStore.setRechargeAmount}
                                                type={'number'}
                                                temp={true}
                                                step={1}
                                                />
                                        </FormField>
                                        {!walletStore.validateRechargeAmountEditor().isValid && walletStore.validateRechargeAmountEditor().messages.map(message => (
                                            <FontBody2 key={message} color={FONT_COLOR.SEMANTIC_DANGER}>{message}</FontBody2>
                                        ))}
                                    </div>
                                </div>

                                {walletStore.shouldShowAutoChargeNote && (
                                    <Note type={NOTE_TYPE.INFORMATION}
                                        body={<>
                                            <div><FontBody1 variant={FONT_VARIANT.BOLD}>{"Upcoming Charge"}</FontBody1></div>
                                            <FontBody2>
                                                {`Your current balance is ${walletStore.amountFormattedWithSymbol}, which is below the minimum threshold you set of ${walletStore.walletCurrencySymbol}${walletStore.rechargeThreshold}.`
                                                    + `To bring your balance up to the minimum, your payment method (${walletStore.brand} ****${walletStore.last4}) will be charged ${walletStore.walletCurrencySymbol}${walletStore.rechargeAmount} `
                                                    + "once you confirm these changes to your auto-recharge settings."}
                                            </FontBody2>
                                        </>} />
                                )}
                            </FormFieldLineLayout>
                        </div>
                    </div>
                    <div className="auto-recharge-settings-modal-buttons">
                        <Button text={"Set up later"} onClick={onCloseClick} variant={BUTTON_VARIANTS.Tertiary} disabled={false} />
                        <Button 
                            text={"Save"} 
                            onClick={walletStore.updateWalletWithAutoDeposit}
                            variant={BUTTON_VARIANTS.Primary} 
                            isLoading={walletStore.isUpdating} 
                            hasLoader={true}
                            disabled={!walletStore.validateMinimumThresholdEditor().isValid || !walletStore.validateRechargeAmountEditor().isValid}
                        />
                    </div>
                </div>
            </DefaultModalLayout>
        </>
    );
});

export default AutoRechargeSettingsModal;